<script lang="ts">
  import LL, { locale } from '$i18n/i18n-svelte';
  import type ERAR from '$lib/erar/spec';
  import { entries } from '$lib/utils/object';
  import { createTabs, melt } from '@melt-ui/svelte';
  import { cubicInOut } from 'svelte/easing';
  import { crossfade } from 'svelte/transition';
  import ItemImage from '../item/item-image.svelte';
  import { getCreditEventName, idToCreditEvent } from '$lib/er/credit/credits';
  import SkillTree from '$lib/er/skill/skill-tree.svelte';
  import { getContext } from 'svelte';
  import {
    type MatchStatisticsContext,
    matchStatisticsContext,
  } from './match-details-content.svelte';
  import RankTierText from '../rank-tiers/rank-tier-text.svelte';
  import { getRankTier } from '../rank-tiers/calculation';
  import { isAfter1_0ByVersion, isNormal } from '../season/season';
  import { formatDate } from '$lib/utils/locale-date';

  export let match: ERAR.Schema.MatchParticipation;

  const { averageRp } = getContext<MatchStatisticsContext>(matchStatisticsContext);

  const {
    elements: { root, list, content, trigger },
    states: { value },
  } = createTabs({
    defaultValue: 'meta',
  });

  $: triggers = [
    { id: 'meta', title: $LL.players.detail.statisticsTabs.meta.title() },
    { id: 'credits', title: $LL.players.detail.statisticsTabs.credits.title() },
    { id: 'skills', title: $LL.players.detail.statisticsTabs.skills.title() },
    // { id: 'traits', title: $LL.players.detail.statisticsTabs.traits.title() },
  ];

  const [send, receive] = crossfade({
    duration: 250,
    easing: cubicInOut,
  });

  $: seasonId = isAfter1_0ByVersion(match.matchVersion) ? 19 : 1;
</script>

<div use:melt={$root} class="match-details-player-statistics">
  <div use:melt={$list} class="trigger-container">
    {#each triggers as triggerItem}
      <button
        use:melt={$trigger(triggerItem.id)}
        class="trigger"
        class:active={$value === triggerItem.id}
      >
        {#if $value === triggerItem.id}
          <div
            class="tab-indicator"
            in:send={{ key: 'trigger' }}
            out:receive={{ key: 'trigger' }}
          />
        {/if}
        <span>{triggerItem.title}</span>
      </button>
    {/each}
  </div>
  <div use:melt={$content('meta')}>
    <div class="meta">
      <section class="average-rp">
        <h3>
          {$LL.players.detail.averageRp()}{isNormal(match.seasonId) ? ' (현재 시즌 랭크 기준)' : ''}
        </h3>
        <RankTierText rankTier={getRankTier({ mmr: Math.round(averageRp), seasonId })} {seasonId} />
      </section>
      <section>
        <h3>{$LL.players.detail.date()}</h3>
        {formatDate(match.matchFinishedAt, 'yyyy. MM. dd. p', $locale)}
      </section>
      <section>
        <h3>서버</h3>
        {match.matchRegion}
      </section>
      <section>
        <h3>{$LL.players.detail.botCount()}</h3>
        {match.matchBots}
      </section>
    </div>
  </div>
  <div use:melt={$content('credits')}>
    <div class="credits-panel">
      {#if match.transferredItems}
        <div>
          <h3>
            {$LL.players.detail.statisticsTabs.credits.transferredItems()}
          </h3>
          <ul class="transferred-items">
            {#each entries(match.transferredItems) as entry}
              <li>
                <ItemImage id={parseInt(entry[0])} /><span> × {entry[1]}</span>
              </li>
            {/each}
          </ul>
        </div>
      {/if}
      <div class="credit-events">
        {#if match.creditEvents}
          {@const events = entries(match.creditEvents)}
          {@const gains = events.filter((v) => v[1] > 0)}
          {@const uses = events.filter((v) => v[1] < 0)}
          <section>
            <h3>
              {$LL.players.detail.statisticsTabs.credits.gain()}
            </h3>
            <ul>
              {#each gains as entry}
                {@const creditEvent = $idToCreditEvent.get(parseInt(entry[0]))}
                {#if creditEvent}
                  <li>{getCreditEventName($LL, creditEvent.name)}<span>{entry[1]}</span></li>
                {/if}
              {/each}
            </ul>
          </section>
          <section>
            <h3>{$LL.players.detail.statisticsTabs.credits.used()}</h3>
            <ul>
              {#each uses as entry}
                {@const creditEvent = $idToCreditEvent.get(parseInt(entry[0]))}
                {#if creditEvent}
                  <li>{getCreditEventName($LL, creditEvent.name)}<span>{entry[1]}</span></li>
                {/if}
              {/each}
            </ul>
          </section>
        {/if}
      </div>
    </div>
  </div>
  <div use:melt={$content('skills')}>
    <SkillTree characterId={match.characterId} skillIdSeries={match.preferences.skills} />
  </div>
  <!-- <div use:melt={$content('traits')}>Traits</div> -->
</div>

<style lang="postcss">
  @import '$lib/styles/breakpoints.css';

  .match-details-player-statistics {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);

    font-size: var(--size-4);

    @media (--sm) {
      font-size: var(--size-5);
    }

    :global(.skill-tree) {
      margin: 0 auto;
    }

    :global(.match-details-team) {
      background-color: var(--blue-3);
      border-radius: var(--radius-3);
      border-color: var(--blue-7);
    }
  }
  .trigger-container {
    display: flex;
    width: fit-content;
    margin: 0 auto;
    background-color: var(--elevation-3);
    border-radius: var(--radius-full);
    padding: var(--space-1);
    border: 1px solid var(--border-normal);
  }

  .trigger {
    position: relative;
    font-size: var(--size-5);
    border-radius: var(--radius-full);
    background-color: transparent;
    border: none;
    span {
      position: relative;
      z-index: 2;
    }
    /* &.active span {
      color: white;
    } */
  }

  .meta {
    display: flex;

    section {
      padding: 0 var(--space-4);
      &:not(:last-child) {
        border-right: 1px solid var(--border-normal);
      }
    }
  }
  .average-rp :global(img) {
    height: 1.4rem;
  }

  .credits-panel {
    display: flex;
    flex-direction: column;
    gap: var(--space-3);
  }

  .tab-indicator {
    position: absolute;
    z-index: 1;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: var(--background);
    border-radius: var(--radius-full);
  }

  .transferred-items {
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-3);

    & > li {
      display: flex;
      flex-direction: column;
      align-items: center;

      span {
        font-size: var(--size-5);
      }
    }
  }

  .credit-events {
    display: grid;
    grid-template-columns: 1fr 1fr;

    section {
      &:first-child {
        padding-right: var(--space-4);
        border-right: 1px solid var(--border-normal);
      }
      &:last-child {
        padding-left: var(--space-4);
      }
    }

    li {
      display: grid;
      grid-template-columns: 1fr auto;
    }
  }

  h3 {
    margin-bottom: var(--space-2);
  }
</style>
